import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput, CustomTextArea, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import DayPicker from 'components/day-picker/DayPicker';
import { CalendarIcon, OutlinedInfoIcon } from 'assets/icons';
import { ReactComponent as EditIcon } from 'assets/icons/EditIcon.svg';
import DateUtils from 'utils/dateUtils';
import { AdjustmentFormData, SuggestedAdjustment } from '../payrun.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { AdjustmentSourceType, AdjustmentType, SuggestedAdjustmentType } from '../constants';
import { Assignee } from 'containers/jobs/jobs.model';

interface SuggestedAdjustmentFormProps {
  suggestedAdjustment: SuggestedAdjustment;
  onDiscard: () => void;
  onDecideLater: (adjustmentData: AdjustmentFormData, isEdited: boolean) => void;
  onAddAdjustment: (adjustmentData: AdjustmentFormData, isEdited: boolean) => void;
  sourceType: AdjustmentSourceType;
  assignees: Assignee[];
  isLoading: boolean;
}

interface AdjustmentData {
  title: string;
  amount: string;
  dateOfPosting: Date;
  description: string;
  type: AdjustmentType;
  member: Assignee;
}

const SuggestedAdjustmentForm: FC<SuggestedAdjustmentFormProps> = ({
  suggestedAdjustment,
  onDiscard,
  onDecideLater,
  onAddAdjustment,
  sourceType,
  assignees = [],
  isLoading
}) => {
  const [editingField, setEditingField] = useState<keyof AdjustmentData | null>(null);
  const [adjustmentData, setAdjustmentData] = useState<AdjustmentData>({
    title: suggestedAdjustment?.title,
    amount: suggestedAdjustment?.amount,
    dateOfPosting: suggestedAdjustment?.postedDate ? new Date(suggestedAdjustment?.postedDate) : new Date(),
    description: suggestedAdjustment?.description,
    type: suggestedAdjustment?.type === SuggestedAdjustmentType.TIP ? AdjustmentType.EARNING : AdjustmentType.DEDUCTION,
    member: assignees?.find(assignee => assignee.id === suggestedAdjustment?.merchantId) || null
  });
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    setAdjustmentData({
      title: suggestedAdjustment?.title,
      amount: suggestedAdjustment?.amount,
      dateOfPosting: suggestedAdjustment?.postedDate ? new Date(suggestedAdjustment?.postedDate) : new Date(),
      description: suggestedAdjustment?.description,
      type:
        suggestedAdjustment?.type === SuggestedAdjustmentType.TIP ? AdjustmentType.EARNING : AdjustmentType.DEDUCTION,
      member: assignees?.find(assignee => assignee.id === suggestedAdjustment?.merchantId) || null
    });
  }, [suggestedAdjustment]);

  const onEditClick = (field: keyof AdjustmentData) => {
    setEditingField(field);
  };

  const handleInputChange = (field: keyof AdjustmentData, value: any) => {
    setAdjustmentData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsEdited(true);
  };

  const transformAdjustmentData = () => {
    const { title, amount, dateOfPosting, description, type } = adjustmentData;
    const payload = {
      title: title.trim(),
      amount: adjustmentData.type === AdjustmentType.EARNING ? amount : `-${amount}`,
      postedDate: dateOfPosting.toISOString(),
      notes: description.trim(),
      ...(sourceType === AdjustmentSourceType.JOB ? { merchantId: adjustmentData.member?.id } : {})
    };
    return payload;
  };

  const handleDecideLater = () => {
    onDecideLater(transformAdjustmentData(), isEdited);
  };

  const handleAddAdjustment = () => {
    onAddAdjustment(transformAdjustmentData(), isEdited);
  };

  const renderField = (field: keyof AdjustmentData, label: string, isEditable = true) => {
    const isEditing = editingField === field;

    if (!isEditing) {
      let displayValue: React.ReactNode;

      if (field === 'amount') {
        displayValue = (
          <span className="font-semibold text-primaryText">{getAmountWithCurrency(adjustmentData[field])}</span>
        );
      } else if (field === 'dateOfPosting') {
        displayValue = (
          <div className="flex items-center">
            <span className="font-semibold text-primaryText">
              {DateUtils.getDateInFormat({ date: adjustmentData[field], dateFormat: 'MMM d, yyyy' })}
            </span>
          </div>
        );
      } else if (field === 'member') {
        displayValue = <span className="py-0.5 font-semibold text-primaryText">{adjustmentData[field]?.name}</span>;
      } else {
        displayValue = <span className="font-semibold text-primaryText">{String(adjustmentData[field])}</span>;
      }

      return (
        <div className="flex items-center justify-between rounded-md border border-secondaryBtn p-3">
          {displayValue}
          {isEditable && (
            <button
              onClick={() => onEditClick(field)}
              className="flex items-center justify-center"
              aria-label={`Edit ${label.toLowerCase()}`}
              tabIndex={0}>
              <EditIcon className="path-fill-current h-5 w-5 text-tertiaryText" />
            </button>
          )}
        </div>
      );
    }

    // Render edit form
    return (
      <div className="">
        {field === 'title' && (
          <CustomInput
            id="adjustment-title"
            name="title"
            value={adjustmentData.title}
            onChange={value => handleInputChange('title', value)}
            placeholder="Enter title"
            className="h-[48px] w-full"
            maxLength={30}
            autoFocus
          />
        )}

        {field === 'amount' && (
          <div className="flex w-full items-center justify-between gap-2">
            <div
              className="flex w-[100px] shrink-0 flex-row items-center 
                  rounded-md text-sbase font-semibold text-primaryText">
              <TooltipWrapper tooltip="Earning" className="h-12 w-1/2">
                <div
                  onClick={() => handleInputChange('type', AdjustmentType.EARNING)}
                  className={`${
                    adjustmentData.type === AdjustmentType.EARNING
                      ? 'bg-[#A4D7FA]'
                      : 'border border-borderGray bg-primary'
                  } flex h-full w-full cursor-pointer items-center justify-center rounded-l-md`}>
                  <div className="text-xl">+</div>
                </div>
              </TooltipWrapper>

              <TooltipWrapper tooltip="Deduction" className="h-12 w-1/2">
                <div
                  onClick={() => handleInputChange('type', AdjustmentType.DEDUCTION)}
                  className={`${
                    adjustmentData.type === AdjustmentType.DEDUCTION
                      ? 'bg-[#A4D7FA]'
                      : 'border border-borderGray bg-primary'
                  } flex h-full w-full cursor-pointer items-center justify-center rounded-r-md`}>
                  <div className="text-xl">-</div>
                </div>
              </TooltipWrapper>
            </div>
            <CustomInput
              id="adjustment-amount"
              name="amount"
              value={adjustmentData.amount.toString()}
              onChange={value => handleInputChange('amount', parseFloat(value) || 0)}
              placeholder="0.00"
              type={InputType.NUMBER}
              className="h-[48px] w-full pl-7"
              prefixWrapperStyle="left-3 text-accentText"
              prefix="$"
              autoFocus
              wrapperClassName="w-full"
            />
          </div>
        )}

        {field === 'dateOfPosting' && (
          <DayPicker
            prefixIcon={<CalendarIcon className="path-fill-current h-[18px] w-[18px] text-accentText" />}
            date={adjustmentData.dateOfPosting}
            onSelect={date => handleInputChange('dateOfPosting', date)}
            labelClassName="font-semibold text-sbase text-primaryText"
            showPastDates={true}
            className="z-20"
          />
        )}

        {field === 'description' && (
          <CustomTextArea
            id="adjustment-description"
            name="description"
            value={adjustmentData.description}
            onChange={value => handleInputChange('description', value)}
            placeholder="Enter description"
            className="h-[80px] w-full"
            maxLength={1000}
            autoFocus
          />
        )}
      </div>
    );
  };

  return (
    <div className="w-full max-w-lg rounded-md bg-white p-4">
      <h3 className="mb-2 text-sbase font-bold text-heading">Suggested adjustment</h3>

      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sbase font-semibold text-primaryText">Title</label>
          {renderField('title', 'Title')}
        </div>

        <div>
          <label className="mb-2 block text-sbase font-semibold text-primaryText">Amount</label>
          {renderField('amount', 'Amount')}
          {suggestedAdjustment?.subText && (
            <p className="mt-2 text-px13 text-accentText">{suggestedAdjustment?.subText}</p>
          )}
        </div>

        <If condition={sourceType === AdjustmentSourceType.JOB}>
          <div>
            <label className="mb-2 block text-sbase font-semibold text-primaryText">Team</label>
            {renderField('member', 'Team', false)}
          </div>
        </If>
        <div>
          <label className="mb-2 block text-sbase font-semibold text-primaryText">Date of posting</label>
          {renderField('dateOfPosting', 'Date of posting')}
        </div>

        <div>
          <label className="mb-2 block text-sbase font-semibold text-primaryText">Description</label>
          {renderField('description', 'Description')}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-4">
        <CustomButton
          id="discard-button"
          type={ButtonType.SECONDARY}
          onClick={onDiscard}
          disabled={isLoading}
          className="h-9 border border-[#D95945] text-[#D95945]">
          Discard
        </CustomButton>
        <CustomButton
          id="decide-later-button"
          type={ButtonType.SECONDARY}
          onClick={handleDecideLater}
          disabled={isLoading}
          className="h-9">
          <div className="flex items-center gap-2">
            Decide later
            <TooltipWrapper tooltip="Adjustment will be moved to next pay run" className="z-0">
              <OutlinedInfoIcon className="path-fill-current shrink-0 text-accentText" />
            </TooltipWrapper>
          </div>
        </CustomButton>
        <CustomButton
          id="add-adjustment-button"
          onClick={handleAddAdjustment}
          type={ButtonType.PRIMARY}
          disabled={isLoading}
          className="h-9">
          Add adjustment
        </CustomButton>
      </div>
    </div>
  );
};

export default SuggestedAdjustmentForm;
